import { useMessageSource } from "react-message-source";
import { NamedParams } from "react-message-source/dist/lib/messages";

interface MessageSourceWithFallback {
  /**
   * This function translates the key into it's i18 translations if key exists. If not it goes to fallback value.
   *
   * @param messageKey The i18 key.
   * @param fallbackValue If no i18 key is found then this value will be used.
   * @return string The translated/fall back value.
   */
  getMessageOrFallback: (messageKey: string, fallbackValue?: string) => string;

  /**
   * This function translates the key into it's i18 translations if key exists. If not it goes to fallback value.
   *
   * @param messageKey The i18 key.
   * @param params The parameters which will be replaced in the i18 key.
   * @param fallbackValue If no i18 key is found then this value will be used.
   * @return string The translated/fall back value.
   */
  getMessageWithNamedParamsOrFallback: (
    messageKey: string,
    params: NamedParams,
    fallbackValue?: string,
  ) => string;
}

const useMessageSourceWithFallback = (): MessageSourceWithFallback => {
  const { getMessage, getMessageWithNamedParams } = useMessageSource();

  const getMessageOrFallback = (messageKey: string, fallbackValue?: string) => {
    const resolvedMessage = getMessage(messageKey);
    return resolvedMessage === messageKey
      ? (fallbackValue ?? resolvedMessage)
      : resolvedMessage;
  };

  const getMessageWithNamedParamsOrFallback = (
    messageKey: string,
    params: NamedParams,
    fallbackValue?: string,
  ) => {
    const resolvedMessage = getMessageWithNamedParams(messageKey, params);
    return resolvedMessage === messageKey
      ? (fallbackValue ?? resolvedMessage)
      : resolvedMessage;
  };

  return { getMessageOrFallback, getMessageWithNamedParamsOrFallback };
};

export default useMessageSourceWithFallback;
